import React, { PropsWithChildren } from "react";
import Grid from '@mui/material/Grid2';
import { Box, SxProps, Theme, styled } from "@mui/material";
import Typography from "@common/components/Typography";

export interface LabeledElementProps {
  label?: string;
  sx?: SxProps<Theme>;
  flex?: number;
}
export interface LabeledTextProps extends LabeledElementProps {
  value?: string | number;
  fullText?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
}));

export const LabeledElement = (
  props: PropsWithChildren<LabeledElementProps>
) => {
  const { label = "", flex = 1, children } = props;
  const sx = Array.isArray(props.sx) ? props.sx : [props.sx];

  return (
    <Container sx={[...sx, { flexGrow: flex, overflow: "hidden" }]}>
      <Typography
        variant="caption"
        sx={({ palette }) => ({ color: palette.common.grey.gBFBFBF })}
      >
        {label}
      </Typography>
      {children}
    </Container>
  );
};
const LabeledText = (props: LabeledTextProps) => {
  const { value = "", fullText, ...elementProps } = props;

  return (
    <LabeledElement {...elementProps}>
      <Typography
        reserveSpace
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          overflowWrap: "break-word",
          whiteSpace: fullText ? undefined : "nowrap",
        }}
      >
        {value}
      </Typography>
    </LabeledElement>
  );
};

export interface LabeledElementGroupProps {
  items: (Omit<LabeledTextProps, "value"> & { value?: React.ReactNode })[];
  selfContained?: boolean;
}

const SelfContainedParent = ({
  children,
  selfContained,
}: PropsWithChildren<{ selfContained: boolean }>) =>
  selfContained ? (
    <Grid container direction="row" wrap="nowrap" sx={{ width: '100%' }}>
      {children}
    </Grid>
  ) : (
    <>{children}</>
  );

const isText = (x?: React.ReactNode): x is string | number | undefined =>
  ["string", "number", "undefined"].includes(typeof x);
export const LabeledElementGroup = (props: LabeledElementGroupProps) => {
  const { items, selfContained = false } = props;
  const flexSum = items.reduce((acc, { flex }) => acc + (flex ?? 1), 0);
  const asArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : [sx]);
  return (
    <SelfContainedParent selfContained={selfContained}>
      {items.map(({ value, ...item }, index) => {
        const width = `${Math.round(((item.flex ?? 1) / flexSum) * 100)}%`;
        const props = {
          ...item,
          key: item.label ?? index,
          sx: [...asArray(item.sx), { width, maxWidth: width }],
        };
        return isText(value) ? (
          <LabeledText {...props} key={props.key} value={value} />
        ) : (
          <LabeledElement {...props} key={props.key}>
            {value}
          </LabeledElement>
        );
      })}
    </SelfContainedParent>
  );
};

export default LabeledText;
